// The month's money, computed from recorded payments.
//
// Pure, and separate from the view, because this is a screen that reports
// numbers to a person who will act on them. An earlier version of this
// multiplied the number of people who had paid by a hardcoded $50, which is not
// a measurement: it would have reported the same total whether somebody paid
// $50, $25 or $500, and it would have reported money that was never collected
// if the dues amount ever changed.
//
// Everything below either comes from a real row or is not shown.

/**
 * @param {Array} members   rows from /membership/members
 * @param {Array} payments  rows from /membership-payments
 * @param {Date}  now
 */
export function summarisePayments(members = [], payments = [], now = new Date()) {
  const thisMonth = monthKey(now);

  const byMethod = {};
  let comp = 0;
  let sponsored = 0;
  let unaccounted = 0;

  members.forEach((member) => {
    // A sponsored member is counted under the method their SPONSOR pays by.
    //
    // "Sponsored" is a relationship, not a way money reaches the club: Mike's
    // card is charged twice a month and both charges are subscriptions. Filing
    // Lailee under her own bucket split the roster into methods that no longer
    // summed to the membership - the club had seventy members and sixty-six
    // accounted for - and the missing four were the question an admin then had
    // to go and answer by hand.
    let method = member.payment_method || null;
    if (method === 'SPONSORED') {
      sponsored += 1;
      // Falls back to nothing rather than to a guess. An unrecorded sponsor
      // method is a gap we can close by looking, not one to paper over.
      method = member.sponsor_payment_method || null;
    }

    if (method) {
      byMethod[method] = (byMethod[method] || 0) + 1;
      if (method === 'COMP') comp += 1;
    } else {
      unaccounted += 1;
    }
  });

  // Attributed to the month the payment COVERS, not the day it arrived. Somebody
  // paying September's dues on August 30 has paid for September.
  const thisMonthsPayments = payments.filter(
    (payment) => payment.payment_month && monthKey(new Date(payment.payment_month)) === thisMonth
  );

  const collected = thisMonthsPayments.reduce(
    (total, payment) => total + (Number(payment.amount) || 0),
    0
  );

  // People, not payments. Somebody who paid three months up front is one member
  // covered, and counting rows would overstate how many are current.
  const paidThisMonth = new Set(thisMonthsPayments.map((payment) => payment.user_id)).size;

  // A complimentary member is not expected to pay. Everybody else is, whether or
  // not we have recorded how they do it.
  const expected = Math.max(0, members.length - comp);

  return {
    total: members.length,
    comp,
    expected,
    byMethod,
    // How many of the counts above are somebody else's payment, and how many
    // members are in none of them. Both exist so the breakdown can say out loud
    // that it adds up, instead of leaving an admin to check.
    sponsored,
    unaccounted,
    collected,
    paidThisMonth,
    // Capped at 100. A payment from somebody who is not counted as expected -
    // a former member, or a guest paying dues - can push paidThisMonth above
    // expected, and an uncapped percentage becomes a progress bar wider than
    // its own track.
    progress: expected > 0 ? Math.min(100, Math.round((paidThisMonth / expected) * 100)) : 0
  };
}

function monthKey(date) {
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getUTCFullYear()}-${date.getUTCMonth()}`;
}
